import { useContext, useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import { FaMoon, FaSun } from 'react-icons/fa'
import { MdOutlineAccountBalanceWallet } from 'react-icons/md'
import { GiHamburgerMenu } from 'react-icons/gi'
import { ImCross } from 'react-icons/im'

import { StateContext } from '~~/src/context/context'
import useDarkMode from '~~/src/hooks/deepcity-ux/useDarkMode'

import { useAccount, } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useLoaded } from '~~/src/hooks/deepcity-ux'
import { Skeleton } from '@mui/material'
import useIntersectionObserver from '~~/src/hooks/deepcity-ux/useIntersectionObserver'
const HamburgerMenu = dynamic(() => import('./HamburgerMenu'), { ssr: false })

const style = {
  wrapper: ` justify-self-end -mt-12
  z-40 flex`,
  iconContainer: `flex flex-row space-x-2 sm:space-x-6`,
  anchor: `focus:outline-none focus:ring active:text-opacity-75 inline-block rounded-full bg-gradient-to-r p-[2px] -mt-1`,
  createBtn: `from-blue-500 via-blue-400 to-blue-300 text-slate-100 hover:via-blue-500 hover:text-white`,
  signBtn: `group from-pink-500 via-red-500 to-yellow-500 text-slate-500 hover:text-white`,
  linkTag: `block rounded-full px-2 py-1 text-xl sm:text-2xl font-medium hover:bg-transparent cursor-pointer`,
  headerIcon: ` text-slate-800 dark:text-[#b2becd] text-2xl cursor-pointer h-12 w-12 max-w-32 pl-4 group`,
  buttonContainer: `z-10 w-10`,
  button: `text-2xl text-black dark:text-[#b2becd]`,
}

export default function NavComponents(): JSX.Element {
  const { displayMenu, setDisplayMenu } =
    useContext(StateContext)
  const { address, isConnected } = useAccount()
  const loaded = useLoaded()
  const router = useRouter()
  const [darkTheme, setDarkTheme] = useDarkMode()
  const handleMode = () => setDarkTheme(!darkTheme)
  const [shouldLoad, targetRef] = useIntersectionObserver();
  const [loadSignInButton, targetSignInSkeleton] = useIntersectionObserver()

  // Handle sideNav on route change
  useEffect(() => {
    if (displayMenu.state) {
      setDisplayMenu({ ...displayMenu, state: false, value: '' })
    }
  }, [router.asPath])

  const create_explore = (
    <div>
      <Link href={`/item/chain`} aria-label='Mint'
        className={`${style.anchor} ${style.createBtn}`}>
        <span className={style.linkTag}>Create</span>
      </Link>
    </div>
  )

  const account = (loadSignInButton ?
    <ConnectButton.Custom>
      {({ account, chain, openAccountModal, openConnectModal, mounted }) => {
        const connected = mounted && account && chain;
        return (
          <>
            {connected ? (
              <div
                key="loginDiv"
                className="group relative flex items-center justify-center"
              >
                <div className={`${style.headerIcon} mr-3 pb-1`}>
                  <button
                    aria-label='Wallet icon'
                    onClick={(e) => {
                      e.preventDefault()
                      setDisplayMenu({
                        ...displayMenu,
                        value: 'Wallet',
                        state: !displayMenu.state,
                      })
                    }}
                  >
                    <MdOutlineAccountBalanceWallet className='text-[1.5rem] sm:text-[2rem]' />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div
                  className={`${style.anchor} ${style.signBtn} `}
                >
                  <button
                    className={`${style.linkTag} bg-white`}
                    key="loginbutton"
                    onClick={openConnectModal}
                    type='button'
                  >
                    Sign in
                  </button>
                </div>
              </div>
            )}
          </>
        )
      }}
    </ConnectButton.Custom> :
    (
      <div className={`${style.headerIcon}`} ref={targetSignInSkeleton}>
        <Skeleton variant='circular' width={30} height={30} className={`dark:bg-slate-400 `} />
      </div>
    )
  )

  const myProfile = (shouldLoad ?
    <div className={`hidden sm:block`}>
      {(address !== undefined) &&
        <Link href={`/profile/${address}`} aria-label='My profile'>
          <div className='text-[#f1f6fc] text-2xl cursor-pointer max-w-32 group
            rounded-full bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 w-8 h-8 p-1'>
            <>
              <span className="tooltip -ml-4 mt-10 group-hover:scale-100">My profile</span>
            </>
          </div>
        </Link>
      }
    </div> :
    <div className={`${style.headerIcon} hidden sm:block`} ref={targetRef}>
      <Skeleton variant='circular' width={30} height={30} className={`dark:bg-slate-400 `} />
    </div>
  )

  const themeSwitcher = (
    <div className={`${style.headerIcon} hidden sm:block`}>
      <span onClick={handleMode} className={`group text-yellow-200`}>
        {darkTheme && loaded ? (
          <FaSun className='text-3xl' />
        ) : (
          <FaMoon className="text-[#869099] text-3xl" />
        )}
        <span className="tooltip -ml-4 mt-6 group-hover:scale-100">Dark mode</span>
      </span>
    </div>
  )

  const Hamburger = (
    <div className={style.buttonContainer}>
      <button
        className={style.button}
        aria-label='Hamburger menu'
        onClick={() => {
          setDisplayMenu({
            ...displayMenu,
            state: !displayMenu.state,
            value: '',
          })
        }}
      >
        {displayMenu.state ? (
          <ImCross className="my-1 text-2xl" />
        ) : (
          <GiHamburgerMenu className='text-3xl' />
        )}
      </button>
    </div>
  )

  return (
    <div className={style.wrapper}>
      <div className={style.iconContainer}>
        {create_explore}
        {account}
        {myProfile}
        {themeSwitcher}
        {Hamburger}
      </div>
      <HamburgerMenu />
    </div>
  )
}